import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  FiSend,
  FiZap,
  FiStopCircle,
  FiTerminal,
  FiAlertCircle,
  FiList,
  FiMonitor,
  FiMic,
} from 'react-icons/fi'
import { Alert, Badge, Box, Typography, Popover } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import FilePreviewEnhanced from './FilePreviewEnhanced'
import LengthSelector from '../LengthSelector'
import { AttachFileOutlined } from '@mui/icons-material'
import CallOverlay from './CallOverlay'
import { useAuthContext } from '../../contexts/AuthContext'
import { useChatStore } from '../../stores/useChatStore'
import { useUIStore } from '../../stores/useUIStore'
import { useCallStore } from '../../stores/useCallStore'
import { useChatActions } from '../../hooks/useChatActions'
import { useCallActions } from '../../hooks/useCallActions'
import { useFileHandling } from '../../hooks/useFileHandling'
import {
  ActionButtonWrapper,
  BottomWrapper,
  ChatInputWrapper,
  ChatTextarea,
  DropOverlay,
  EnhanceButton,
  FileInputLabel,
  FilterPopoverContent,
  FormContainer,
  IconSmallButton,
  Knob,
  PopoverTitle,
  SendButton,
  SpinnerIcon,
  StopButton,
  SwitchContainer,
  TextareaIconsRight,
  ToggleItemRow,
  ToggleLabel,
  TokenUsageDisplay,
} from './ChatInput.styles'

const MAX_FILES = 5

const ChatInput = () => {
  const { handleSendMessage, handleMagicPrompt, handleStopProcess } = useChatActions()
  const { startCall, startScreenShare } = useCallActions()
  const { user, loading: authLoading } = useAuthContext()

  const {
    newMessage,
    isLoadingMessages,
    isSendingMessage,
    lastTokenUsage,
    setNewMessage,
  } = useChatStore(
    useShallow(state => ({
      newMessage: state.newMessage,
      isLoadingMessages: state.isLoadingMessages,
      isSendingMessage: state.isSendingMessage,
      lastTokenUsage: state.lastTokenUsage,
      setNewMessage: state.setNewMessage,
    }))
  )

  const {
    selectedFiles,
    isDragging,
    enableDeepAnalysis,
    showTokenUsage,
    magicPromptLength,
    isMagicLoading,
    inputFocusSignal,
    setEnableDeepAnalysis,
    setMagicPromptLength,
  } = useUIStore(
    useShallow(state => ({
      selectedFiles: state.selectedFiles,
      isDragging: state.isDragging,
      enableDeepAnalysis: state.enableDeepAnalysis,
      showTokenUsage: state.showTokenUsage,
      magicPromptLength: state.magicPromptLength,
      isMagicLoading: state.isMagicLoading,
      inputFocusSignal: state.inputFocusSignal,
      setEnableDeepAnalysis: state.setEnableDeepAnalysis,
      setMagicPromptLength: state.setMagicPromptLength,
    }))
  )

  const {
    isCallActive,
    isCallLockedByOtherTab,
    isConnecting,
    isStartingScreen,
    streamingMessageId,
  } = useCallStore(
    useShallow(state => ({
      isCallActive: state.isCallActive,
      isCallLockedByOtherTab: state.isCallLockedByOtherTab,
      isConnecting: state.isConnecting,
      isStartingScreen: state.isStartingScreen,
      streamingMessageId: state.streamingMessageId,
    }))
  )
  const { chatInputRef, fileInputRef, dropZoneRef, handleFileChange, removeFile } =
    useFileHandling(MAX_FILES)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)

  useEffect(() => {
    if (!streamingMessageId) {
      chatInputRef.current?.focus()
    }
  }, [streamingMessageId, chatInputRef, inputFocusSignal])

  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget)
  }

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
  }

  const openFilter = Boolean(filterAnchorEl)

  return (
    <Box sx={{ paddingInline: '1rem' }}>
      <ChatInputWrapper>
        <FormContainer
          ref={dropZoneRef}
          onSubmit={handleSendMessage}
          isDragging={isDragging}
        >
          {isDragging && <DropOverlay>Trascina qui i file...</DropOverlay>}

          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 3, px: 2 }}>
              <FilePreviewEnhanced
                selectedFiles={selectedFiles}
                onRemoveFile={removeFile}
              />
            </Box>
          )}

          <ChatTextarea
            ref={chatInputRef}
            placeholder={
              isSendingMessage
                ? 'F.R.A.N.C.A. sta rispondendo...'
                : 'Es: Progetta un data center a 4 piani con tetto a pannelli solari...'
            }
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            disabled={isLoadingMessages || authLoading || isMagicLoading}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage(e)
              }
            }}
          />

          <BottomWrapper>
            <ActionButtonWrapper>
              <FileInputLabel title={`Allega file (max ${MAX_FILES})`}>
                <IconSmallButton component="span">
                  {selectedFiles.length > 0 ? (
                    <Badge badgeContent={selectedFiles.length} color="primary">
                      <AttachFileOutlined />
                    </Badge>
                  ) : (
                    <AttachFileOutlined />
                  )}
                </IconSmallButton>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={isSendingMessage}
                  accept=".pdf,.txt,.md,.docx,.doc,.csv,.json,.xml,.png,.jpg,.jpeg,.webp,.xls,.xlsx"
                />
              </FileInputLabel>

              <Tooltip title="Filtri e Opzioni Avanzate">
                <IconSmallButton
                  aria-label="filter"
                  onClick={handleFilterClick}
                  sx={{
                    backgroundColor: openFilter ? 'action.selected' : 'transparent',
                  }}
                >
                  <FiList />
                </IconSmallButton>
              </Tooltip>

              <Popover
                open={openFilter}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <FilterPopoverContent>
                  <PopoverTitle>Opzioni Avanzate</PopoverTitle>

                  <ToggleItemRow
                    onClick={() => setEnableDeepAnalysis(!enableDeepAnalysis)}
                  >
                    <ToggleLabel>Analisi Profonda</ToggleLabel>
                    <SwitchContainer
                      sx={{
                        backgroundColor: enableDeepAnalysis ? 'primary.main' : '#ccc',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={enableDeepAnalysis}
                        onChange={() => {}}
                        disabled={isSendingMessage}
                      />
                      <Knob
                        className="knob"
                        sx={{
                          transform: enableDeepAnalysis
                            ? 'translateX(20px)'
                            : 'translateX(0)',
                        }}
                      />
                    </SwitchContainer>
                  </ToggleItemRow>

                  {enableDeepAnalysis && (
                    <Alert severity="warning" icon={<FiAlertCircle />} sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        L'opzione <strong>Profonda</strong> è attivata. Questo può
                        rallentare la risposta, ma aumenta la precisione.
                      </Typography>
                    </Alert>
                  )}

                  {showTokenUsage && lastTokenUsage && (
                    <TokenUsageDisplay
                      title={`Prompt: ${lastTokenUsage.prompt_tokens}, Completamento: ${lastTokenUsage.completion_tokens}`}
                    >
                      <FiTerminal /> {lastTokenUsage.total_tokens}{' '}
                      {lastTokenUsage.estimated && '(stima)'}
                    </TokenUsageDisplay>
                  )}
                </FilterPopoverContent>
              </Popover>

              {!isCallActive && (
                <>
                  {user?.feature_flags?.includes('call_feature') && (
                    <Tooltip
                      title={
                        isConnecting
                          ? 'Connessione...'
                          : isCallLockedByOtherTab
                          ? "Chiamata già attiva in un'altra scheda"
                          : 'Inizia chiamata vocale'
                      }
                    >
                      <span>
                        <IconSmallButton
                          aria-label="voice mode"
                          onClick={() => startCall()}
                          disabled={
                            isConnecting || isStartingScreen || isCallLockedByOtherTab
                          }
                        >
                          {isConnecting ? <SpinnerIcon /> : <FiMic />}
                        </IconSmallButton>
                      </span>
                    </Tooltip>
                  )}

                  {user?.feature_flags?.includes('screen_share') && (
                    <Tooltip
                      title={
                        isStartingScreen ? 'Avvio condivisione...' : 'Condividi schermo'
                      }
                    >
                      <span>
                        <IconSmallButton
                          aria-label="share screen"
                          onClick={() => startScreenShare()}
                          disabled={isConnecting || isStartingScreen}
                        >
                          {isStartingScreen ? <SpinnerIcon /> : <FiMonitor />}
                        </IconSmallButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </ActionButtonWrapper>

            <TextareaIconsRight>
              <LengthSelector
                selectedLength={magicPromptLength}
                onLengthChange={setMagicPromptLength}
                disabled={isSendingMessage || isMagicLoading}
              />
              <EnhanceButton
                type="button"
                onClick={handleMagicPrompt}
                disabled={!newMessage.trim() || isSendingMessage || isMagicLoading}
                title="Migliora la domanda con l'IA"
              >
                {isMagicLoading ? <SpinnerIcon /> : <FiZap />}
              </EnhanceButton>

              {isSendingMessage ? (
                <StopButton
                  type="button"
                  onClick={handleStopProcess}
                  title="Interrompi Processo"
                >
                  <FiStopCircle />
                </StopButton>
              ) : (
                <SendButton
                  type="submit"
                  disabled={
                    isLoadingMessages ||
                    authLoading ||
                    (!newMessage.trim() && selectedFiles.length === 0)
                  }
                  title="Invia Messaggio"
                >
                  <FiSend />
                </SendButton>
              )}
            </TextareaIconsRight>
          </BottomWrapper>
        </FormContainer>
      </ChatInputWrapper>
      <CallOverlay />
    </Box>
  )
}

export default ChatInput
